import { useSelector } from "react-redux";
import {
  getOverallAccountBalance,
  selectAccountBalance,
  selectLoadingAccountBalance,
} from "./components/OverallAccountsBalanceSlice";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { selectGlobalSettings } from "app/modules/system/settings/ClientPortalSettingsSlice";

const EXPIRY_PERIOD_MS = 1000 * 60 * 5; // 5 minutes

const OverallAccountBalance: React.FC = () => {
  const thunkDispatch = useThunkDispatch();

  const globalSettings = useSelector(selectGlobalSettings);
  const balance = useSelector(selectAccountBalance);
  const isLoading = useSelector(selectLoadingAccountBalance);
  const getInTouchLink = useSelector(selectGlobalSettings).getInTouchLink;

  const updateOverallAccountBalance = useCallback(() => {
    thunkDispatch(getOverallAccountBalance());
  }, [thunkDispatch]);

  useEffect(() => {
    updateOverallAccountBalance();
    const interval = setInterval(updateOverallAccountBalance, EXPIRY_PERIOD_MS);
    return () => {
      clearInterval(interval);
    };
  }, [updateOverallAccountBalance]);

  const getBalanceTextColour = () => {
    if (balance > 1000) {
      return "cpl-text-danger";
    } else if (balance > 500) {
      return "cpl-text-warning-dark";
    } else {
      return "";
    }
  };

  return (
    <div className="card">
      <ActivityIndicator isBusy={isLoading}>
        <div className="card-body">
          <div className="row">
            <div className="col-12 col-lg-6 pe-lg-4 mb-3 mb-lg-0">
              <div className="d-flex flex-column">
                <p className="heading-text fs-5">Current account balance</p>
                <div className="d-flex justify-content-between">
                  {!isLoading && <span className={`fs-2 ${getBalanceTextColour()}`}>{balance.toGBPString()}</span>}
                  {globalSettings.showPayments && (
                    <Link to="/Payments/PayNow" data-test-id="account-balance__pay-now-link">
                      <button type="button" className="cpl-btn cpl-btn-primary">
                        Pay Now
                      </button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="cpl-card cpl-bg-warning px-4 py-3">
                If you are experiencing problems with making this payment, please{" "}
                {getInTouchLink ? (
                  <a
                    href={getInTouchLink}
                    target="_blank"
                    rel="noreferrer"
                    data-test-id="account-balance__get-in-touch-link"
                  >
                    get in touch
                  </a>
                ) : (
                  "get in touch"
                )}{" "}
                at the earliest opportunity to discuss how we can help.
              </div>
            </div>
          </div>
        </div>
      </ActivityIndicator>
    </div>
  );
};

export default OverallAccountBalance;
